// src/modules/purityAnalyzer/pages/TestHistory.jsx
// Searchable, branch-scoped test list. HO sees all branches + Branch column
// and a branch filter; branch users see only their own tests (server-enforced).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Input, Select, Tag, Typography, Space, Button, DatePicker,
} from 'antd';
import AdvancedTable from '../../../components/UI/AdvancedTable';
import { SearchOutlined, ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchTests } from '../api';
import useBranchScope from '../hooks/useBranchScope';
import KaratBadge from '../components/KaratBadge';
import { XRF_STATUS_COLORS, purityColor } from '../constants/purityConstants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function TestHistory() {
  const navigate = useNavigate();
  const { isHO, branches } = useBranchScope();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    page: 1, limit: 20, search: '', branchId: undefined,
    xrfStatus: undefined, dateFrom: undefined, dateTo: undefined,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTests(query);
      const data = res?.data?.data || {};
      setRows(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      title: 'Test #', dataIndex: 'testNumber', width: 110,
      render: (v, r) => <a onClick={() => navigate(`/purity/tests/${r._id}`)}>{v}</a>,
    },
    {
      title: 'Purity', dataIndex: 'predictedPurity', width: 110,
      sorter: (a, b) => a.predictedPurity - b.predictedPurity,
      render: (v) => (
        <Text strong style={{ color: purityColor(v), fontVariantNumeric: 'tabular-nums' }}>
          {v?.toFixed(2)}%
        </Text>
      ),
    },
    {
      title: 'Karat', dataIndex: 'estimatedKarat', width: 90,
      render: (v, r) => <KaratBadge karat={v} fineness={r.karatFineness} />,
    },
    {
      title: 'Density', dataIndex: 'goldDensity', width: 100,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v} g/cm³</span>,
    },
    {
      title: 'Confidence', dataIndex: 'confidenceScore', width: 100,
      render: (v) => `${v}%`,
    },
    { title: 'Customer', dataIndex: ['customer', 'name'], render: (v) => v || '—' },
    {
      title: 'Transaction', dataIndex: ['transactionRef', 'label'],
      render: (v) => v || '—',
    },
    ...(isHO ? [{ title: 'Branch', dataIndex: ['branch', 'name'], width: 130 }] : []),
    { title: 'Operator', dataIndex: ['operator', 'name'], width: 120 },
    {
      title: 'XRF', dataIndex: 'xrfStatus', width: 100,
      render: (v) => <Tag color={XRF_STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: (v) => (v === 'REVERSED' ? <Tag color="red">REVERSED</Tag> : <Tag color="green">OK</Tag>),
    },
    {
      title: 'Date', dataIndex: 'createdAt', width: 150,
      render: (v) => dayjs(v).format('DD MMM YY · HH:mm'),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <ExperimentOutlined style={{ marginRight: 10 }} />Purity Test History
        </Title>
        <Button type="primary" onClick={() => navigate('/purity/run')}>Run New Test</Button>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input
            prefix={<SearchOutlined />} allowClear placeholder="Test # / txn / sample"
            style={{ width: 220 }}
            onChange={(e) => setQuery((q) => ({ ...q, page: 1, search: e.target.value }))}
          />
          {isHO && (
            <Select
              allowClear placeholder="All branches" style={{ width: 180 }}
              options={branches.map((b) => ({ value: b._id, label: b.name }))}
              onChange={(v) => setQuery((q) => ({ ...q, page: 1, branchId: v }))}
            />
          )}
          <Select
            allowClear placeholder="XRF status" style={{ width: 140 }}
            options={['PENDING', 'VERIFIED', 'SKIPPED'].map((s) => ({ value: s, label: s }))}
            onChange={(v) => setQuery((q) => ({ ...q, page: 1, xrfStatus: v }))}
          />
          <RangePicker
            onChange={(range) =>
              setQuery((q) => ({
                ...q, page: 1,
                dateFrom: range?.[0]?.startOf('day')?.toISOString(),
                dateTo: range?.[1]?.endOf('day')?.toISOString(),
              }))
            }
          />
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </Card>

      <AdvancedTable
        rowKey="_id"
        size="small"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: query.page, pageSize: query.limit, total,
          showSizeChanger: true, showTotal: (t) => `${t} tests`,
          onChange: (page, limit) => setQuery((q) => ({ ...q, page, limit })),
        }}
      />
    </div>
  );
}
